import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, Send, User, RotateCcw } from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface TutorViewProps {
  skillNodeId: string;
  courseId?: string;
  nodeTitle: string;
}

const GREEN_DEEP = "#1C3829";
const LIME       = "hsl(65,65%,75%)";

// ── localStorage helpers ──────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10); // "2026-03-17"
}
function chatKey(userId: string, nodeId: string) {
  return `tutor_chat_${userId}_${nodeId}`;
}
function loadChat(userId: string, nodeId: string): Message[] | null {
  try {
    const raw = localStorage.getItem(chatKey(userId, nodeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.date !== todayStr()) return null; // different day → treat as empty
    return Array.isArray(parsed.messages) ? parsed.messages : null;
  } catch { return null; }
}
function saveChat(userId: string, nodeId: string, messages: Message[]) {
  try {
    localStorage.setItem(chatKey(userId, nodeId), JSON.stringify({ date: todayStr(), messages }));
  } catch { /* ignore quota errors */ }
}
function clearChat(userId: string, nodeId: string) {
  localStorage.removeItem(chatKey(userId, nodeId));
}

export default function TutorView({ skillNodeId, courseId, nodeTitle }: TutorViewProps) {
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input, setInput]         = useState("");
  const [opening, setOpening]     = useState(true);  // true while fetching opening msg
  const [sending, setSending]     = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const inputRef                  = useRef<HTMLTextAreaElement>(null);
  const userIdRef                 = useRef<string | null>(null);

  // ─── Scroll to bottom on each new message ────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // ─── Call the edge function ───────────────────────────────────────────────
  const callTutor = useCallback(async (msgs: Message[], isOpening = false): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");

    const { data, error } = await supabase.functions.invoke("course-assistant", {
      body: {
        messages: msgs,
        context: {
          courseId:   courseId ?? null,
          courseName: null,
          nodeId:     skillNodeId,
          nodeName:   nodeTitle,
          pageType:   "learner",
        },
        mode:           "tutor",
        generateOpening: isOpening,
        _authToken:     session.access_token,
      },
    });

    if (error) throw error;
    return (data?.message as string) || "";
  }, [skillNodeId, courseId, nodeTitle]);

  // ─── Generate opening question (always clears history) ──────────────────
  const startSession = useCallback(async () => {
    if (userIdRef.current) clearChat(userIdRef.current, skillNodeId);
    setMessages([]);
    setInput("");
    setOpening(true);
    try {
      const reply = await callTutor([], true);
      setMessages([{ role: "assistant", content: reply }]);
    } catch {
      setMessages([{
        role: "assistant",
        content: `Welcome! Let's explore **${nodeTitle}** together. What do you already know about this topic?`,
      }]);
    } finally {
      setOpening(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [callTutor, nodeTitle, skillNodeId]);

  // ─── Save messages to localStorage after every update ────────────────────
  useEffect(() => {
    if (userIdRef.current && messages.length > 0) {
      saveChat(userIdRef.current, skillNodeId, messages);
    }
  }, [messages, skillNodeId]);

  // ─── On mount / node change: restore today's session or start fresh ───────
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session) { startSession(); return; }
      userIdRef.current = session.user.id;
      const stored = loadChat(session.user.id, skillNodeId);
      if (stored && stored.length > 0) {
        setMessages(stored);
        setOpening(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        startSession();
      }
    };
    init();
    return () => { cancelled = true; };
  }, [skillNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Send a user message ─────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || opening) return;
    setInput("");
    setSending(true);
    const updated: Message[] = [...messages, { role: "user", content: text }];
    setMessages(updated);
    try {
      const reply = await callTutor(updated);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I ran into a problem. Please try again.",
      }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 100px)",   // header (56px) + tab bar (44px)
      maxWidth: 720, margin: "0 auto",
    }}>

      {/* ── Message list ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px 16px 8px" }}>

        {/* Splash while fetching opening */}
        {opening && (
          <div style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            minHeight: 240, gap: 16,
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: 30,
              background: GREEN_DEEP, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BookOpen style={{ width: 26, height: 26, color: LIME }} />
            </div>
            <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 14 }}>
              Your tutor is preparing a question…
            </p>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              marginBottom: 16,
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            {/* Tutor avatar */}
            {msg.role === "assistant" && (
              <div style={{
                width: 34, height: 34, borderRadius: 17, flexShrink: 0,
                background: GREEN_DEEP,
                display: "flex", alignItems: "center", justifyContent: "center",
                marginTop: 4,
              }}>
                <BookOpen style={{ width: 15, height: 15, color: LIME }} />
              </div>
            )}

            {/* Bubble */}
            <div style={{
              maxWidth: "74%",
              background: msg.role === "user"
                ? GREEN_DEEP
                : "hsl(var(--muted))",
              color: msg.role === "user"
                ? "#EAE6DC"
                : "hsl(var(--foreground))",
              borderRadius: msg.role === "user"
                ? "18px 18px 4px 18px"
                : "4px 18px 18px 18px",
              padding: "12px 16px",
              fontSize: 14,
              lineHeight: 1.65,
            }}>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              </div>
            </div>

            {/* User avatar */}
            {msg.role === "user" && (
              <div style={{
                width: 34, height: 34, borderRadius: 17, flexShrink: 0,
                background: "hsl(var(--muted))",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginTop: 4,
              }}>
                <User style={{ width: 15, height: 15 }} />
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {sending && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 17, flexShrink: 0,
              background: GREEN_DEEP,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BookOpen style={{ width: 15, height: 15, color: LIME }} />
            </div>
            <div style={{
              background: "hsl(var(--muted))",
              borderRadius: "4px 18px 18px 18px",
              padding: "14px 18px",
              display: "flex", gap: 5, alignItems: "center",
            }}>
              {[0, 0.18, 0.36].map((delay, i) => (
                <span key={i} className="tutor-typing-dot" style={{ animationDelay: `${delay}s` }} />
              ))}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input area ── */}
      <div style={{
        borderTop: "1px solid hsl(var(--border))",
        padding: "14px 16px 16px",
        background: "hsl(var(--background))",
      }}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply to your tutor…"
            disabled={opening || sending}
            rows={1}
            style={{ resize: "none", flex: 1, maxHeight: 120, minHeight: 44 }}
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || opening || sending}
            size="icon"
            style={{ background: GREEN_DEEP, color: LIME, flexShrink: 0, height: 44, width: 44 }}
          >
            <Send style={{ width: 16, height: 16 }} />
          </Button>
        </div>
        <div style={{
          display: "flex", justifyContent: "space-between",
          marginTop: 8, alignItems: "center",
        }}>
          <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
            Enter to send · Shift+Enter for new line
          </p>
          <button
            onClick={startSession}
            disabled={opening || sending}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 11, color: "hsl(var(--muted-foreground))",
              background: "none", border: "none", cursor: "pointer",
              opacity: opening || sending ? 0.4 : 1,
            }}
          >
            <RotateCcw style={{ width: 11, height: 11 }} /> Restart session
          </button>
        </div>
      </div>
    </div>
  );
}
